import React, { useState, useEffect } from 'react';
import organisationSettingsService, { OrganisationSettings, UpdateOrganisationSettingsRequest, Language } from '../../services/organisationSettingsService';
import useAuthStore from '../../store/authStore';
import { useTheme } from '../../contexts/ThemeContext';
import './OrganisationSettingsForm.css';

interface OrganisationSettingsFormProps {
  onUpdate?: (settings: OrganisationSettings) => void;
  onError?: (error: string) => void;
}

const OrganisationSettingsForm: React.FC<OrganisationSettingsFormProps> = ({ 
  onUpdate, 
  onError 
}) => {
  const { user } = useAuthStore();
  const { theme } = useTheme();
  const [settings, setSettings] = useState<OrganisationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    validationUpper: 0,
    validationMiddle: 0,
    validationLower: 0,
    logoUrl: '',
    defaultLanguage: 'en',
    aiModel: 'Premium'
  });

  const [supportedLanguages, setSupportedLanguages] = useState<Language[]>([]);

  // Logo upload states
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Add global error handler to catch any unhandled errors
  useEffect(() => {
    const handleUnhandledError = (event: ErrorEvent) => {
      console.error('Unhandled error detected that might cause page reload:', event.error);
      console.error('Error details:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection detected that might cause page reload:', event.reason);
    };

    window.addEventListener('error', handleUnhandledError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleUnhandledError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Load current settings
  useEffect(() => {
    const loadSettings = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const currentSettings = await organisationSettingsService.getOrganisationSettings();
        setSettings(currentSettings);
        setFormData({
          validationUpper: currentSettings.validationUpper,
          validationMiddle: currentSettings.validationMiddle,
          validationLower: currentSettings.validationLower,
          logoUrl: currentSettings.logoUrl || '',
          defaultLanguage: currentSettings.defaultLanguage || 'en',
          aiModel: currentSettings.aiModel || 'Premium'
        });

        // Set logo preview if logo URL exists
        if (currentSettings.logoUrl) {
          // Construct full URL if it's a relative path
          // currentSettings.logoUrl is now something like /api/organisation-settings/logo/{orgId}
          const baseUrl = currentSettings.logoUrl.startsWith('http') 
            ? currentSettings.logoUrl 
            : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${currentSettings.logoUrl}`;
          // Add cache-busting parameter to ensure fresh image load
          const logoUrl = `${baseUrl}?t=${Date.now()}`;
          console.log('Setting logo preview URL:', logoUrl);
          console.log('Original logoUrl from database:', currentSettings.logoUrl);
          setLogoPreview(logoUrl);
        }
      } catch (err: any) {
        console.error('Error loading organisation settings:', err);
        const errorMessage = err.message || 'Failed to load organisation settings';
        setError(errorMessage);
        if (onError) onError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [user, onError]);

  // Load supported languages
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        console.log('🔄 Loading supported languages...');
        const languages = await organisationSettingsService.getSupportedLanguages();
        console.log('✅ Loaded languages:', languages);
        setSupportedLanguages(languages);
      } catch (error: any) {
        console.error('❌ Failed to load supported languages:', error);
        console.error('❌ Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        // Set default languages if API fails
        setSupportedLanguages([
          { code: 'en', name: 'English', nativeName: 'English' }
        ]);
      }
    };

    loadLanguages();
  }, []);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    if (field === 'defaultLanguage' || field === 'logoUrl' || field === 'aiModel') {
      // Handle string fields
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      // Handle numeric fields
      const numValue = parseInt(value) || 0;
      setFormData(prev => ({
        ...prev,
        [field]: numValue
      }));
    }
  };

  // Logo upload handlers
  const handleLogoFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Please select a JPEG, PNG, GIF, or WebP image.');
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setError('File is too large. Maximum size is 5MB.');
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };



  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setFormData(prev => ({
      ...prev,
      logoUrl: '' // This will clear the logo URL in the database when saved
    }));
    setError('Logo will be removed when you save settings.');
    setTimeout(() => setError(null), 3000);
  };

  const validateForm = (): string | null => {
    const { validationUpper, validationMiddle, validationLower } = formData;

    if (validationUpper < 0 || validationMiddle < 0 || validationLower < 0) {
      return 'All validation values must be non-negative';
    }

    if (validationUpper < validationMiddle || validationMiddle < validationLower) {
      return 'Validation values must be in descending order: Upper ≥ Middle ≥ Lower';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      if (onError) onError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      let logoUrlToSave = formData.logoUrl;

      // If there's a pending logo file, upload it first
      if (logoFile) {
        console.log('Uploading logo file during save:', logoFile.name, logoFile.size, logoFile.type);
        
        try {
          const result = await organisationSettingsService.uploadLogo(logoFile);
          logoUrlToSave = result.logoUrl;
          console.log('Logo uploaded successfully:', result.logoUrl);
          
          // Update preview with uploaded logo
          // logoUrlToSave is now something like /api/organisation-settings/logo/{orgId}
          const baseUrl = logoUrlToSave.startsWith('http') 
            ? logoUrlToSave 
            : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${logoUrlToSave}`;
          // Add cache-busting parameter to ensure fresh image load
          const fullLogoUrl = `${baseUrl}?t=${Date.now()}`;
          console.log('Updating logo preview after upload:', fullLogoUrl);
          console.log('Original logoUrlToSave:', logoUrlToSave);
          setLogoPreview(fullLogoUrl);
          setLogoFile(null); // Clear the pending file
        } catch (logoError: any) {
          console.error('Error uploading logo during save:', logoError);
          throw new Error(`Failed to upload logo: ${logoError.message}`);
        }
      }

      console.log('Saving settings with logo URL:', logoUrlToSave);

      const updates: UpdateOrganisationSettingsRequest = {
        validationUpper: formData.validationUpper,
        validationMiddle: formData.validationMiddle,
        validationLower: formData.validationLower,
        logoUrl: logoUrlToSave || null, // Send null to clear the logo from database
        defaultLanguage: formData.defaultLanguage,
        aiModel: formData.aiModel
      };

      console.log('Updates to send:', updates);

      const updatedSettings = await organisationSettingsService.updateOrganisationSettings(updates);
      
      console.log('Updated settings received:', updatedSettings);
      
      // Update form data with the final logo URL
      setFormData(prev => ({
        ...prev,
        logoUrl: updatedSettings.logoUrl || ''
      }));
      
      // Update logo preview with the new logo URL
      if (updatedSettings.logoUrl) {
        const baseUrl = updatedSettings.logoUrl.startsWith('http') 
          ? updatedSettings.logoUrl 
          : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${updatedSettings.logoUrl}`;
        // Add cache-busting parameter to ensure fresh image load
        const fullLogoUrl = `${baseUrl}?t=${Date.now()}`;
        console.log('Updating logo preview after settings save:', fullLogoUrl);
        setLogoPreview(fullLogoUrl);
      } else {
        console.log('Clearing logo preview - no logo URL in settings');
        setLogoPreview(null); // Clear preview if logo was removed
      }
      
      setSettings(updatedSettings);
      if (onUpdate) onUpdate(updatedSettings);
      
      // Dispatch custom event to notify sidebar and other components to refresh logo
      console.log('About to dispatch organizationLogoUpdated event');
      const logoUpdateEvent = new CustomEvent('organizationLogoUpdated', {
        detail: { logoUrl: updatedSettings.logoUrl }
      });
      window.dispatchEvent(logoUpdateEvent);
      console.log('organizationLogoUpdated event dispatched successfully');
      
      // Show success message briefly
      setError('Settings updated successfully!');
      setTimeout(() => setError(null), 3000);
      
      console.log('Settings save completed successfully, no errors detected');
      
    } catch (err: any) {
      console.error('Error updating organisation settings:', err);
      const errorMessage = err.message || 'Failed to update organisation settings';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setFormData({
        validationUpper: settings.validationUpper,
        validationMiddle: settings.validationMiddle,
        validationLower: settings.validationLower,
        logoUrl: settings.logoUrl || '',
        defaultLanguage: settings.defaultLanguage || 'en',
        aiModel: settings.aiModel || 'Premium'
      });
      setLogoFile(null);
      // Construct full URL if it's a relative path
      const logoUrl = settings.logoUrl 
        ? (settings.logoUrl.startsWith('http') 
            ? `${settings.logoUrl}?t=${Date.now()}`
            : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${settings.logoUrl}?t=${Date.now()}`)
        : null;
      setLogoPreview(logoUrl);
      setError(null);
    }
  };

  if (loading) {
    return (
      <div className="org-settings-form" data-theme={theme}>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading organisation settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="org-settings-form" data-theme={theme}>
      <div className="form-header">
        <h3>Validation Thresholds</h3>
        <p className="form-description">
          Set the validation thresholds for your organisation. Values should be in descending order.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="validationUpper">
              Validation Upper
              <span className="field-description">Maximum acceptable value</span>
            </label>
            <input
              id="validationUpper"
              type="number"
              min="0"
              value={formData.validationUpper}
              onChange={(e) => handleInputChange('validationUpper', e.target.value)}
              className="form-input"
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="validationMiddle">
              Validation Middle
              <span className="field-description">Warning threshold value</span>
            </label>
            <input
              id="validationMiddle"
              type="number"
              min="0"
              value={formData.validationMiddle}
              onChange={(e) => handleInputChange('validationMiddle', e.target.value)}
              className="form-input"
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="validationLower">
              Validation Lower
              <span className="field-description">Minimum acceptable value</span>
            </label>
            <input
              id="validationLower"
              type="number"
              min="0"
              value={formData.validationLower}
              onChange={(e) => handleInputChange('validationLower', e.target.value)}
              className="form-input"
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="defaultLanguage">
              Default Language
              <span className="field-description">Default language for reports and interface</span>
            </label>
            <select
              id="defaultLanguage"
              value={formData.defaultLanguage}
              onChange={(e) => handleInputChange('defaultLanguage', e.target.value)}
              className="form-select"
              disabled={saving}
            >
              {supportedLanguages.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.name} ({language.nativeName})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="aiModel">
              AI Model
              <span className="field-description">AI model tier for enhanced capabilities</span>
            </label>
            <select
              id="aiModel"
              value={formData.aiModel}
              onChange={(e) => handleInputChange('aiModel', e.target.value)}
              className="form-select"
              disabled={saving}
            >
              <option value="Premium">Premium (Claude 4.5 - 1M context)</option>
              <option value="Premium Small">Premium Small (Claude 4.5 - 200K context)</option>
              <option value="Standard">Standard (Nova Pro - 200K context)</option>
            </select>
          </div>
        </div>

        {/* Logo Upload Section */}
        <div className="form-section">
          <h4>Organisation Logo</h4>
          <p className="form-description">
            Upload your organisation logo. Accepted formats: JPEG, PNG, GIF, WebP (Max 5MB)
            <br />
            <strong>Process:</strong> Select file → Click "Save Settings" → Logo uploads automatically to server
          </p>
          
          <div className="logo-upload-container">
            <div className="logo-preview-section">
              {logoPreview && (
                <div className="logo-preview">
                  <img 
                    key={logoPreview} 
                    src={logoPreview} 
                    alt="Logo preview" 
                    className="logo-image"
                    onLoad={() => console.log('Logo preview image loaded:', logoPreview)}
                    onError={(e) => console.error('Logo preview image failed to load:', logoPreview, e)}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="remove-logo-btn"
                    disabled={saving}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
            
            <div className="logo-upload-controls">
              <input
                type="file"
                id="logoUpload"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleLogoFileSelect}
                className="file-input"
                disabled={saving}
              />
              <label htmlFor="logoUpload" className="file-input-label">
                Choose Logo File
              </label>
              
              {logoFile && (
                <div className="logo-status">
                  📁 New logo selected: {logoFile.name}
                  <br />
                  <small>Will be uploaded when you save settings</small>
                </div>
              )}
              
              {formData.logoUrl && !logoFile && (
                <div className="current-logo-section">
                  <div className="logo-status">
                    ✅ Current logo: {formData.logoUrl.split('/').pop()}
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="remove-logo-button"
                    disabled={saving}
                  >
                    🗑️ Remove Logo
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className={`message ${error.includes('successfully') ? 'success' : 'error'}`}>
            {error}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="btn-secondary"
            disabled={saving}
          >
            Reset
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {settings && (
        <div className="settings-info">
          <h4>Current Settings</h4>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Last Updated:</span>
              <span className="info-value">
                {new Date(settings.updatedAt).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Organization ID:</span>
              <span className="info-value">{settings.organizationId}</span>
            </div>
            {settings.logoUrl && (
              <div className="info-item">
                <span className="info-label">Logo URL:</span>
                <span className="info-value">{settings.logoUrl}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganisationSettingsForm;
